/**
 * The stored shape of a notification, shared by the server data module and
 * the client feed. Client-safe: no server imports.
 *
 * The silo stores the validator columns at the top level and packs every
 * other key into `extraFields`. A read lifts `extraFields` back onto the row
 * and drops the `extraFields` key, so a feed row carries its extras as
 * top-level keys (`integrationReview`, `actionApproval`, `continuityHandoff`,
 * ...). Rows a client builds itself still nest them.
 */

/** The columns the silo notifications validator accepts at the top level. */
export const NOTIFICATION_STORED_COLUMNS: ReadonlySet<string> = new Set([
  "id",
  "userId",
  "companyId",
  "source",
  "vendor",
  "summary",
  "summaryAudio",
  "notificationType",
  "presentationMode",
  "dedupeKey",
  "groupKey",
  "surfacePriority",
  "digestDateKey",
  "sourceObjectType",
  "sourceObjectId",
  "urgency",
  "urgentCount",
  "actionRequired",
  "categories",
  "attentionCandidates",
  "recommendedActions",
  "topPriorityItem",
  "topPriorityEvent",
  "contextualInsights",
  "escalationMessages",
  "storedDate",
  "createdDate",
  "updatedDate",
  "expiresAt",
  "extraFields",
]);

/**
 * The other columns of the silo notifications table, which a caller never
 * reads or writes: the plaintext battery-runway feed column (the silo derives
 * it on every plaintext write) and the field-crypto engine columns. The data
 * module strips them from every row it returns and every document it writes.
 * With `NOTIFICATION_STORED_COLUMNS` they are exactly the table's columns.
 */
export const NOTIFICATION_SILO_MAINTAINED_COLUMNS = [
  "batteryRunwayFeed",
  "keyCheck",
  "keyedContentDigest",
  "logicalId",
  "sealedLayoutVersion",
] as const;

const SILO_MAINTAINED: ReadonlySet<string> = new Set(NOTIFICATION_SILO_MAINTAINED_COLUMNS);

/** Keys the feed response adds to a row (feed contract v1); never stored. */
const FEED_RESPONSE_KEYS: ReadonlySet<string> = new Set(["presentation"]);

function plainRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * A notification's extras as one nested record, whichever shape the row is
 * in: the keys a feed row carries outside the table's columns, with a nested
 * `extraFields` (a client-built row) winning on a clash. Keys starting with
 * `_` are client bookkeeping, not extras. Null when the row has none.
 */
export function notificationExtraFields(row: unknown): Record<string, unknown> | null {
  const source = plainRecord(row);
  if (!source) return null;
  const extras: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(source)) {
    if (NOTIFICATION_STORED_COLUMNS.has(key) || SILO_MAINTAINED.has(key) || FEED_RESPONSE_KEYS.has(key) || key.startsWith("_") || value === undefined) continue;
    extras[key] = value;
  }
  Object.assign(extras, plainRecord(source.extraFields));
  return Object.keys(extras).length ? extras : null;
}
